/**
 * Lista de Empleados
 * Fecha: 16 Oct 2025
 *
 * Vista principal con tabla, filtros, búsqueda y paginación
 */
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { employeesService } from '@/features/rh/api/employees.service';
import type {
  Employee,
  EmployeeFilters,
  AreaPuestoOption,
} from '@/types/employee.types';
import { PermissionGuard, ProgressBar } from '@/shared/components';
import {
  Users,
  Search,
  CirclePlus,
  FolderOpen,
  Shield,
  ChartColumn,
  Percent,
  PenLine,
} from "lucide-react";
import {
  EMPLOYEE_PERMISSIONS,
} from '@/shared/hooks/usePermissions';
import { userHasPermission } from '@/shared/lib/roleUtils';
import { useAuth } from '@/shared/hooks/useAuth';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import { EsqueletoLinea, EsqueletoTabla } from "@/shared/components/Esqueleto";
import FilterSelect from "@/shared/components/FilterSelect";
import { Badge } from "@/shared/components/Badge";
import KpiCard from "@/features/rh/components/dashboard/KpiCard";

const ITEMS_PER_PAGE = 20;

export const EmployeesList: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filteredEmployees, setFilteredEmployees] = useState<Employee[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterUniqueAreaJobOptions, setFilterUniqueAreaJobOptions] = useState<
    AreaPuestoOption[]
  >([]);

  // Filters & Search
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<EmployeeFilters>({});
  const [areaOptions, setAreaOptions] = useState<
    { id: number; nombre: string }[]
  >([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Permissions
  // const canCreate = hasPermission(EMPLOYEE_PERMISSIONS.EMPLOYEES_CREATE);
  const canUpdate = userHasPermission(
    "rh.empleados_expedientes.base.update",
    user?.rbac?.effectivePermissions || [],
  );
  const canRead = userHasPermission(
    "rh.empleados_expedientes.base.read",
    user?.rbac?.effectivePermissions || [],
  );

  useEffect(() => {
    loadEmployees();
  }, [filters]);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, employees]);

  const extractUniqueAreaPuesto = (employees: any[]): AreaPuestoOption[] => {
    const uniqueMap = new Map<string, AreaPuestoOption>();

    employees.forEach((emp) => {
      const area = emp.area?.nombre || "Sin Departamento/Área";
      const puesto = emp.puesto?.nombre || "Sin puesto";
      const puestoId = emp?.puestoId;
      const key = `${area}-${puesto}`; // clave única por combinación

      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, {
          id: uniqueMap.size + 1,
          area,
          puesto,
          puestoId,
        });
      }
    });

    return Array.from(uniqueMap.values());
  };

  const loadEmployees = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await employeesService.getAll(filters);
      const dataWithoutFilters = await employeesService.getAll();
      setFilterUniqueAreaJobOptions(
        extractUniqueAreaPuesto(dataWithoutFilters),
      );
      // Las áreas salen de la lista sin filtrar: si salieran de la filtrada,
      // al elegir una se quedaría como única opción del desplegable.
      setAreaOptions(
        Array.from(
          new Map(
            dataWithoutFilters
              .filter((e) => e.area?.id != null)
              .map((e) => [e.area!.id, e.area!.nombre] as const),
          ),
          ([id, nombre]) => ({ id, nombre }),
        ).sort((a, b) => a.nombre.localeCompare(b.nombre, "es")),
      );
      setEmployees(data);
      setFilteredEmployees(data);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error("Error al cargar empleados:", err);
      setError(err.message || "Error al cargar empleados");
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...employees];

    // Búsqueda por texto
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((emp) => {
        const fullName = `${emp.user?.firstName || ""} ${
          emp.user?.lastName || ""
        }`.toLowerCase();
        const email = emp.user?.email?.toLowerCase() || "";
        const curp = emp.curp.toLowerCase();
        return (
          fullName.includes(term) || email.includes(term) || curp.includes(term)
        );
      });
    }

    // Filtro por porcentaje de progreso
    if (filters.completenessPercentage !== undefined) {
      filtered = filtered.filter(
        (emp) =>
          Math.round(emp.completenessPercentage) ===
          filters.completenessPercentage,
      );
    }

    if (filters.puestoId !== undefined) {
      filtered = filtered.filter((emp) => emp.puestoId == filters.puestoId);
    }

    setFilteredEmployees(filtered);
    setCurrentPage(1); // Reset to first page
  };

  const handleFilterChange = (key: keyof EmployeeFilters, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === "" ? undefined : value,
    }));
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm("");
  };

  // Pagination
  const totalPages = Math.ceil(filteredEmployees.length / ITEMS_PER_PAGE);
  const paginatedEmployees = filteredEmployees.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-lg border border-[#E5E7EB] p-4 h-[116px] flex flex-col justify-between"
            >
              <EsqueletoLinea className="w-2/3" />
              <EsqueletoLinea className="w-1/4 h-6" />
            </div>
          ))}
        </div>
        <div className="bg-white rounded-lg border border-[#E5E7EB] overflow-hidden">
          <EsqueletoTabla filas={8} columnas={9} />
        </div>
      </div>
    );
  }

  if (
    userHasPermission(
      "rh.empleados_expedientes.base.read",
      user?.rbac?.effectivePermissions || [],
    ) === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f9fafb] mt-3">
      <div className="w-full">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <TituloDePantalla>
                  Empleados expedientes
                </TituloDePantalla>
              </div>
              <p className="text-gray-600 dark:text-gray-400">
                Gestión del personal de la empresa
              </p>
            </div>

            <PermissionGuard permission={EMPLOYEE_PERMISSIONS.EMPLOYEES_CREATE}>
              <button
                onClick={() => navigate("/hiring/new")}
                className={estiloDeBoton('primario')}
              >
                <CirclePlus className="w-4 h-4" />
                Nuevo Empleado
              </button>
            </PermissionGuard>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <KpiCard
            title="Total Empleados"
            value={filteredEmployees.length}
            icon={<Users />}
            variant="purple"
            isLoading={isLoading}
          />
          <KpiCard
            title="IMSS Habilitado"
            value={employees.filter((e) => e.habilitadoImss).length}
            icon={<Shield />}
            variant="success"
            isLoading={isLoading}
          />
          <KpiCard
            title="ERP Habilitado"
            value={employees.filter((e) => e.habilitadoErp).length}
            icon={<ChartColumn />}
            variant="info"
            isLoading={isLoading}
          />
          <KpiCard
            title="Progreso Promedio"
            value={
              employees.length > 0
                ? Math.round(
                    employees.reduce(
                      (sum, e) => sum + e.completenessPercentage,
                      0,
                    ) / employees.length,
                  )
                : 0
            }
            suffix="%"
            icon={<Percent />}
            variant="secondary"
            isLoading={isLoading}
          />
        </div>

        {/* Filtros con el patrón de almacén: una fila, buscador con lupa,
            desplegables del componente compartido y fechas con el calendario
            azul de la marca. */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap items-center gap-3 mb-6">
          <div className="relative w-full max-w-[320px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar en empleados..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:border-[#315FB9]"
            />
          </div>

          <FilterSelect
            label="Área"
            ancho="contenido"
            options={areaOptions.map((a) => ({
              value: String(a.id),
              label: a.nombre,
            }))}
            value={filters.areaId === undefined ? "" : String(filters.areaId)}
            onChange={(v) =>
              handleFilterChange("areaId", v === "" ? undefined : Number(v))
            }
          />

          <FilterSelect
            label="Puesto"
            ancho="contenido"
            options={(filterUniqueAreaJobOptions ?? []).map(
              (obj: AreaPuestoOption) => ({
                value: String(obj.puestoId),
                label: `${obj.area} ${obj.puesto}`,
              }),
            )}
            value={filters.puestoId === undefined ? "" : String(filters.puestoId)}
            onChange={(v) =>
              handleFilterChange("puestoId", v === "" ? undefined : Number(v))
            }
          />

          <FilterSelect
            label="Progreso"
            ancho="contenido"
            options={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((n) => ({
              value: String(n),
              label: `${n}%`,
            }))}
            value={
              filters.completenessPercentage === undefined
                ? ""
                : String(filters.completenessPercentage)
            }
            onChange={(v) =>
              handleFilterChange(
                "completenessPercentage",
                v === "" ? undefined : Number(v),
              )
            }
          />

          <FilterSelect
            label="Estado IMSS"
            ancho="contenido"
            options={[
              { value: "si", label: "Habilitado" },
              { value: "no", label: "Deshabilitado" },
            ]}
            value={
              filters.habilitadoImss === undefined
                ? ""
                : filters.habilitadoImss
                  ? "si"
                  : "no"
            }
            onChange={(v) =>
              handleFilterChange(
                "habilitadoImss",
                v === "" ? undefined : v === "si",
              )
            }
          />

          <FilterSelect
            label="Estado ERP"
            ancho="contenido"
            options={[
              { value: "si", label: "Habilitado" },
              { value: "no", label: "Deshabilitado" },
            ]}
            value={
              filters.habilitadoErp === undefined
                ? ""
                : filters.habilitadoErp
                  ? "si"
                  : "no"
            }
            onChange={(v) =>
              handleFilterChange(
                "habilitadoErp",
                v === "" ? undefined : v === "si",
              )
            }
          />

          <button
            onClick={clearFilters}
            className="text-sm font-semibold text-[#315FB9] hover:underline"
          >
            Limpiar
          </button>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4 mb-6 shadow-sm">
            <p className="text-red-700 font-semibold">{error}</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredEmployees.length === 0 && (
          <div className="bg-[#ffffff]rounded-lg shadow p-12 text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No se encontraron empleados
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {searchTerm || Object.keys(filters).length > 0
                ? "Intenta ajustar los filtros de búsqueda"
                : "Comienza agregando el primer empleado"}
            </p>
          </div>
        )}

        {/* Employees Table */}
        {filteredEmployees.length > 0 && (
          <>
            <div className="bg-white rounded-lg overflow-hidden border border-1 border-[#E5E7EB]">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-[#E5E7EB]">
                  <thead className="bg-[#FAF9FF]">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                        Empleado
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                        Correo
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                        CURP
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                        Área
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                        Puesto
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-medium text-[#1B2128] tracking-wider">
                        Progreso
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-medium text-[#1B2128] tracking-wider">
                        IMSS
                      </th>
                      <th className="px-6 py-4 text-center text-sm font-medium text-[#1B2128] tracking-wider">
                        ERP
                      </th>
                      <th className="px-6 py-4 text-right text-sm font-medium text-[#1B2128] tracking-wider">
                        Acciones
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-[#ffffff] divide-y divide-[#E1E1E1]">
                    {paginatedEmployees.map((employee) => (
                      <tr
                        key={employee.id}
                        className="hover:bg-[#F4F7FB] transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div
                            className={`${canRead ? "cursor-pointer" : ""} text-sm font-medium text-[#1B2128]`}
                            onClick={() => {
                              if (canRead) {
                                navigate(`/employees/${employee.id}`);
                              }
                            }}
                          >
                            {employee.user?.firstName} {employee.user?.lastName}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div
                            className={`${canRead ? "cursor-pointer" : ""} text-sm text-[#959595]`}
                            onClick={() => {
                              if (canRead) {
                                navigate(`/employees/${employee.id}`);
                              }
                            }}
                          >
                            {employee.user?.email}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div
                            className={`${canRead ? "cursor-pointer" : ""} text-sm text-gray-900 dark:text-white`}
                            onClick={() => {
                              if (canRead) {
                                navigate(`/employees/${employee.id}`);
                              }
                            }}
                          >
                            {employee.curp}
                          </div>
                        </td>
                        <td
                          className={`${canRead ? "cursor-pointer" : ""} px-6 py-4 whitespace-nowrap text-sm text-[#1B2128]`}
                          onClick={() => {
                            if (canRead) {
                              navigate(`/employees/${employee.id}`);
                            }
                          }}
                        >
                          {employee.area?.nombre || "-"}
                        </td>
                        <td
                          className={`${canRead ? "cursor-pointer" : ""} px-6 py-4 whitespace-nowrap text-sm text-[#1B2128]`}
                          onClick={() => {
                            if (canRead) {
                              navigate(`/employees/${employee.id}`);
                            }
                          }}
                        >
                          {employee.puesto?.nombre || "-"}
                        </td>
                        <td
                          className={`${canRead ? "cursor-pointer" : ""} px-6 py-4 whitespace-nowrap text-center`}
                          onClick={() => {
                            if (canRead) {
                              navigate(`/employees/${employee.id}`);
                            }
                          }}
                        >
                          <div className="flex justify-center w-[140px]">
                            <span className="mr-2 text-sm font-medium text-gray-900 dark:text-white">
                              {employee.completenessPercentage}%
                            </span>
                            <ProgressBar
                              value={employee.completenessPercentage}
                              size={"md"}
                              showLabel={false}
                            />
                          </div>
                        </td>
                        <td
                          className={`${canRead ? "cursor-pointer" : ""} px-6 py-4 whitespace-nowrap text-center`}
                          onClick={() => {
                            if (canRead) {
                              navigate(`/employees/${employee.id}`);
                            }
                          }}
                        >
                          <Badge color={employee.habilitadoErp ? "verde" : "gris"}>
                            {employee.habilitadoErp
                              ? "Habilitado"
                              : "Deshabilitado"}
                          </Badge>
                        </td>
                        <td
                          className={`${canRead ? "cursor-pointer" : ""} px-6 py-4 whitespace-nowrap text-center`}
                          onClick={() => {
                            if (canRead) {
                              navigate(`/employees/${employee.id}`);
                            }
                          }}
                        >
                          <Badge color={employee.habilitadoErp ? "verde" : "gris"}>
                            {employee.habilitadoErp
                              ? "Habilitado"
                              : "Deshabilitado"}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end gap-5">
                            {/* El ojo se quitó: la fila entera ya lleva al
                                detalle, así que era un segundo camino al mismo
                                lugar ocupando lugar en la columna. */}
                            {canUpdate && (
                              <button
                                onClick={() =>
                                  navigate(`/employees/${employee.id}?tab=InfoLaboral&autoEdit=true`)
                                }
                                className="text-[#315FB9] hover:text-[#315FB9]/80"
                                title="Editar"
                              >
                                <PenLine className="w-5 h-5" />
                              </button>
                            )}
                            {userHasPermission(
                              "rh.empleados_expedientes.base.update",
                              user?.rbac?.effectivePermissions || [],
                            ) && (
                              <button
                                onClick={() =>
                                  navigate(`/employees/${employee.id}?tab=Expediente`)
                                }
                                className="text-[#315FB9] hover:text-[#315FB9]/80"
                                title="Expediente"
                              >
                                <FolderOpen className="w-5 h-5" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6 rounded-b-lg">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => goToPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => goToPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Mostrando{" "}
                      <span className="font-medium">
                        {(currentPage - 1) * ITEMS_PER_PAGE + 1}
                      </span>{" "}
                      a{" "}
                      <span className="font-medium">
                        {Math.min(
                          currentPage * ITEMS_PER_PAGE,
                          filteredEmployees.length,
                        )}
                      </span>{" "}
                      de{" "}
                      <span className="font-medium">
                        {filteredEmployees.length}
                      </span>{" "}
                      resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      {Array.from(
                        { length: Math.min(5, totalPages) },
                        (_, i) => {
                          let pageNum: number;
                          if (totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (currentPage >= totalPages - 2) {
                            pageNum = totalPages - 4 + i;
                          } else {
                            pageNum = currentPage - 2 + i;
                          }
                          return (
                            <button
                              key={pageNum}
                              onClick={() => goToPage(pageNum)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                currentPage === pageNum
                                  ? "z-10 bg-blue-50 dark:bg-blue-900/30 border-blue-500 text-blue-600 dark:text-blue-400"
                                  : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600"
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        },
                      )}
                      <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
                      >
                        Siguiente
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
